import React, { useCallback, useEffect, useState } from 'react';

import { Alert } from '@zooplus/ui-components';

import RetryPaymentGooglePay from '../RetryPaymentGooglePay/RetryPaymentGooglePay';
import RetryPaymentNotification from '../RetryPaymentNotification/RetryPaymentNotification';

import ActionCreator from '@components/ActionCreator/ActionCreator';
import { Details, ThreeDSDetails } from '@hooks/useActionCreator';
import { useTranslation } from '@hooks/useTranslations';
import { useAppContext } from '@providers/AppContextProvider';
import { useConfig } from '@providers/ConfigProvider';
import { OverlayIds, useOverlays } from '@providers/OverlaysProvider';
import { useRetryState } from '@providers/RetryDataProvider';
import { useRetryPaymentContext } from '@providers/RetryPaymentContextProvider';
import { useThunkDispatch } from '@providers/ThunkDispatchProvider';
import AdobeTrackingService from '@services/AdobeTrackingService';
import { initiatePayment, processPaymentAuthorisation } from '@services/retryPaymentService';
import { PaymentMethod, RetryPaymentMethod } from '@types-internal';
import { InitiateRetryPaymentRequestData } from '@types-internal/RetryTypes';
import {
  getMobileAppType,
  getMobileAppsReturnUrl,
  getOrderDetailsUrl,
  isRecognizedApp,
} from '@utils/retryUtils';
import { convertRunningEnvToAdyenEnv } from '@utils/utils';

import styles from './LocalPaymentsRetryInitiator.module.scss';

const trackRetryInitiateClick = (paymentMethod: RetryPaymentMethod, orderId: string) => {
  AdobeTrackingService.accountEventTracking('retry_payment', {
    payment_method: paymentMethod,
    order_id: orderId,
  });
};

export interface LocalPaymentsRetryInitiatorProps
  extends Omit<InitiateRetryPaymentRequestData, 'returnUrl' | 'hasAuthorizationRedirect'> {
  retryPaymentMethod: RetryPaymentMethod;
  isRetryPossible: boolean;
  currency: string;
  customerId: string;
  siteId: number;
  amount?: number;
  sid?: string;
}

export const LocalPaymentsRetryInitiator: React.FC<LocalPaymentsRetryInitiatorProps> = ({
  orderId,
  customerId,
  retryPaymentMethod,
  isRetryPossible,
  currency,
  siteId,
  amount,
  sid,
}) => {
  const [mobileAppType] = useState(() => {
    return getMobileAppType();
  });
  const { thunkDispatch } = useThunkDispatch();
  const {
    retryState: { initiatePaymentData, processAuthorisationData },
    processPaymentAuthorisationFailure,
  } = useRetryState();
  const {
    retryPaymentInfo: { retryPaymentStatus: retryPaymentStatusFromContext },
  } = useRetryPaymentContext();

  const {
    config: {
      env,
      adyen: { clientKey },
    },
  } = useConfig();
  const { showOverlay } = useOverlays();
  const { t } = useTranslation();
  const {
    config: { appUrlSchema },
  } = useConfig();

  const { locale, shopBundle } = useAppContext();
  const isFetching = initiatePaymentData?.isFetching;
  const retryPaymentStatusFromAuthorization =
    processAuthorisationData?.authorizationResult?.retryPaymentStatus;
  const resultCode = processAuthorisationData?.authorizationResult?.resultCode;
  const isMobileApp = isRecognizedApp(mobileAppType);
  const hasAuthorizationRedirect =
    isMobileApp && (retryPaymentMethod === 'BANCONTACT_MOBILE' || retryPaymentMethod === 'TWINT');
  const isKlarnaPaymentRefused =
    (retryPaymentMethod === 'KLARNA_PAY_LATER' || retryPaymentMethod === 'KLARNA_PAY_OVER_TIME') &&
    resultCode === 'REFUSED';

  const returnUrl = isMobileApp
    ? getMobileAppsReturnUrl(appUrlSchema[mobileAppType], hasAuthorizationRedirect)
    : getOrderDetailsUrl();
  const isQrCodeAction = initiatePaymentData?.actionObject?.type === 'qrCode';
  const authorizationId = initiatePaymentData?.authorizationId;

  const handleInitiatePayment = () => {
    if (!isFetching) {
      trackRetryInitiateClick(retryPaymentMethod, orderId);
      thunkDispatch(initiatePayment({ orderId, customerId, returnUrl, hasAuthorizationRedirect }));
    }
  };
  const handlePaymentDetails = useCallback(
    (details: Details) => {
      if (retryPaymentMethod === 'GOOGLE_PAY') {
        thunkDispatch(
          processPaymentAuthorisation({
            customerId: String(customerId),
            paymentId: initiatePaymentData?.paymentId,
            paymentMethod: 'GOOGLE_PAY',
            threeDSResult: (details as ThreeDSDetails).threeDSResult,
          }),
        );
      }
    },
    [customerId, initiatePaymentData?.paymentId, retryPaymentMethod, thunkDispatch],
  );

  const actionTimedOutHandler = useCallback(() => {
    threeDSFailed();
  }, []);

  const threeDSFailed = useCallback(() => {
    processPaymentAuthorisationFailure();
  }, [processPaymentAuthorisationFailure]);

  useEffect(() => {
    if (hasAuthorizationRedirect && Boolean(authorizationId)) {
      window.location.assign(
        `${window.location.origin}/payment/apppaymentrouter?auth_id=${authorizationId}&termUrl=${returnUrl}`,
      );
    }
  }, [authorizationId, hasAuthorizationRedirect]);

  useEffect(() => {
    if (isQrCodeAction && !hasAuthorizationRedirect) {
      showOverlay(OverlayIds.BANCONTACT_MOBILE_AUTH);
    }
  }, [isQrCodeAction, showOverlay, hasAuthorizationRedirect]);

  const paymentMethodInterpolation =
    retryPaymentMethod === 'BANCONTACT_MOBILE' ? 'bancontact' : retryPaymentMethod.toLowerCase();

  const notificationBannerText = t('mzp.order.detail.payment.reminder.warning', {
    0: t('mzp.order.detail.retry.payment.button', {
      0: t(`general.payment.${paymentMethodInterpolation}`),
    }),
  });

  const ctaButtonText = t('mzp.order.detail.retry.payment.button', {
    0: t(`general.payment.${paymentMethodInterpolation}`),
  });

  let retryPaymentStatus = retryPaymentStatusFromContext;
  if (retryPaymentMethod === 'GOOGLE_PAY') {
    retryPaymentStatus = retryPaymentStatusFromAuthorization ?? retryPaymentStatusFromContext;
  }

  let actionObjectPaymentMethod: PaymentMethod = 'DP';
  if (retryPaymentMethod === 'GOOGLE_PAY') {
    actionObjectPaymentMethod = 'GP';
  }

  const showRetryInitiator =
    isRetryPossible &&
    (retryPaymentStatus === undefined ||
      (retryPaymentStatus !== 'SUCCESSFUL' && retryPaymentStatus !== 'PROCESSING'));

  return (
    <>
      {retryPaymentStatus && (
        <RetryPaymentNotification
          retryPaymentStatus={retryPaymentStatus}
          retryPaymentMethod={retryPaymentMethod}
          resultCode={resultCode}
          orderId={orderId}
          customerId={customerId}
          currency={currency}
          siteId={siteId}
          sid={sid}
        />
      )}
      {initiatePaymentData?.isFailure && (
        <Alert className={styles.retryAlert} variant="warning">
          {t('mzp.order.detail.retry.payment.error.apicall')}
        </Alert>
      )}
      {initiatePaymentData?.actionObject && !isQrCodeAction && !hasAuthorizationRedirect && (
        <ActionCreator
          paymentAction={initiatePaymentData?.actionObject}
          clientKey={clientKey}
          adyenEnvironment={convertRunningEnvToAdyenEnv(env)}
          actionTimedOutHandler={actionTimedOutHandler}
          locale={locale}
          sendPaymentDetailsHandler={handlePaymentDetails}
          paymentMethod={actionObjectPaymentMethod}
          shopBundle={shopBundle}
        />
      )}

      {showRetryInitiator && retryPaymentMethod === 'GOOGLE_PAY' && typeof amount === 'number' && (
        <RetryPaymentGooglePay
          sid={sid}
          currency={currency}
          customerId={customerId}
          amount={amount}
          orderId={orderId}
          siteId={siteId}
        />
      )}

      {showRetryInitiator && !isKlarnaPaymentRefused && retryPaymentMethod !== 'GOOGLE_PAY' && (
        <Alert
          className={styles.retryMainAlert}
          variant="warning"
          id="paymentRedirectButton"
          buttonText={ctaButtonText}
          onButtonClick={handleInitiatePayment}
          aria-label={t('mzp.order.detail.retry.payment.accessibility.label')}>
          {notificationBannerText}
        </Alert>
      )}
    </>
  );
};

export default LocalPaymentsRetryInitiator;
